import React, { useEffect, useState } from 'react';
import { Cookie, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LanguageLink from './LanguageLink';

const CONSENT_STORAGE_KEY = 'costaStoneCookieConsent';

const consentSettings = {
  accepted: {
    ad_storage: 'granted',
    analytics_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted'
  },
  declined: {
    ad_storage: 'denied',
    analytics_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied'
  }
};

const updateGoogleConsent = (choice) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return;
  }

  window.gtag('consent', 'update', consentSettings[choice]);
};

const getSavedChoice = () => {
  try {
    return window.localStorage.getItem(CONSENT_STORAGE_KEY);
  } catch (error) {
    return null;
  }
};

const setSavedChoice = (choice) => {
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, choice);
  } catch (error) {
    // Consent still updates for the current visit if storage is unavailable.
  }
};

const CookieConsent = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const savedChoice = getSavedChoice();

    if (savedChoice === 'accepted' || savedChoice === 'declined') {
      updateGoogleConsent(savedChoice);
      return;
    }

    setIsVisible(true);
  }, []);

  const saveChoice = (choice) => {
    setSavedChoice(choice);
    updateGoogleConsent(choice);
    setIsVisible(false);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="fixed inset-x-0 bottom-0 z-[80] px-4 pb-4 sm:px-6">
      <div className="mx-auto max-w-5xl rounded-lg border border-stone-200 bg-white shadow-2xl">
        <div className="grid gap-4 p-5 md:grid-cols-[auto_1fr_auto] md:items-center">
          <div className="hidden h-11 w-11 items-center justify-center rounded-full bg-[#519dc7]/10 text-[#519dc7] md:flex">
            <Cookie size={22} />
          </div>
          <div>
            <h2 className="text-base font-bold text-stone-900">
              {t('cookies.title')}
            </h2>
            <p className="mt-1 text-sm leading-relaxed text-stone-600">
              {t('cookies.description')}{' '}
              <LanguageLink to="/privacy-policy" className="font-semibold text-[#2f7fa8] hover:text-[#519dc7]">
                {t('cookies.privacyLink')}
              </LanguageLink>
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row md:justify-end">
            <button
              type="button"
              onClick={() => saveChoice('declined')}
              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-md border border-stone-300 px-4 py-2 text-sm font-semibold text-stone-700 transition-colors hover:bg-stone-100"
            >
              <X size={16} />
              {t('cookies.decline')}
            </button>
            <button
              type="button"
              onClick={() => saveChoice('accepted')}
              className="inline-flex min-h-10 items-center justify-center rounded-md bg-[#519dc7] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#3d8bb5]"
            >
              {t('cookies.accept')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
